import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, ParamMap, Route, Router, RouterLink } from '@angular/router';
import { RouterService } from '@core/router/router.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

import { Breadcrumb, BreadcrumbsRouteData, BreadcrumbsService } from './breadcrumbs.typings';

@Injectable({ providedIn: 'root' })
export class RoutingBreadcrumbsService implements BreadcrumbsService {
  private breadcrumbs: BehaviorSubject<Breadcrumb[]> = new BehaviorSubject(null);
  public readonly items: Observable<Breadcrumb[]> = this.breadcrumbs.asObservable();

  constructor(
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private router: Router,
    private routerService: RouterService,
  ) {
    this.routerService
      .selectCurrentRoute()
      .pipe(
        // prevents `BreadcrumbsService#items` being null on browser refresh
        // @see: https://github.com/angular/angular/issues/17473
        startWith(this.router.routerState.snapshot),
        map(() => this.build(this.activatedRoute.root)),
      )
      .subscribe((breadcrumbs) => this.updateItems(breadcrumbs));
  }

  public updateBreadcrumbLabel(route: ActivatedRouteSnapshot, label: string): void {
    const rootUrl: string = this.buildRootUrl(route);

    const updatedBreadcrumbs: Breadcrumb[] = this.breadcrumbs.value.map((breadcrumb) => {
      if (breadcrumb.routerLink === rootUrl) {
        breadcrumb.label = this.translateService.instant(label);
      }

      return breadcrumb;
    });

    this.updateItems(updatedBreadcrumbs);
  }

  public updateItems(items: Breadcrumb[]): void {
    this.breadcrumbs.next(items);
  }

  private build(activatedRoute: ActivatedRoute, url: RouterLink['routerLink'] = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
    const breadcrumb = this.createBreadcrumb(activatedRoute.routeConfig, activatedRoute.snapshot, url);

    if (breadcrumb.label) {
      breadcrumbs.push(breadcrumb);
    }

    if (activatedRoute.firstChild) {
      return this.build(activatedRoute.firstChild, breadcrumb.routerLink, breadcrumbs);
    }

    return breadcrumbs;
  }

  private createBreadcrumb(route: Route, snapshot: ActivatedRouteSnapshot, url: RouterLink['routerLink'] = ''): Breadcrumb {
    let label: BreadcrumbsRouteData['breadcrumb'] = (route?.data as BreadcrumbsRouteData)?.breadcrumb ?? '';
    let path: Route['path'] = route?.path ?? '';

    if (this.isRouteWithParam(path) && !!snapshot) {
      label = this.buildDynamicLabel(path, snapshot.paramMap);
      path = this.buildDynamicPath(path, snapshot.paramMap);
    }

    const routerLink = path ? `${url}/${path}` : url;

    if (label) {
      label = this.translateService.instant(label);
    }

    return {
      label,
      routerLink,
    };
  }

  private isRouteWithParam(path: Route['path']): boolean {
    return path.split('/').pop().startsWith(':');
  }

  private buildDynamicLabel(path: Route['path'], params: ParamMap): string {
    const lastRoutePart = path.split('/').pop();
    const paramName = lastRoutePart.split(':')[1];

    return params.get(paramName);
  }

  private buildDynamicPath(path: Route['path'], params: ParamMap): Route['path'] {
    const lastRoutePart = path.split('/').pop();
    const paramName = lastRoutePart.split(':')[1];

    return path.replace(lastRoutePart, params.get(paramName));
  }

  private buildRootUrl(route: ActivatedRouteSnapshot): string {
    let url = '';
    let next = route.root;

    while (next.firstChild !== null) {
      next = next.firstChild;

      if (next.routeConfig === null) {
        continue;
      }
      if (!next.routeConfig.path) {
        continue;
      }

      url += `/${this.buildUrl(next)}`;

      if (next === route) {
        break;
      }
    }

    return url;
  }

  private buildUrl(route: ActivatedRouteSnapshot): string {
    return route.url.map((s) => s.toString()).join('/');
  }
}
